<template>
  <div>
    <v-card flat>
      <v-card-text>
        <v-row dense>
          <v-col
            v-for="language in items"
            :key="language.id"
            cols="12"
            sm="6"
            md="4"
            class="d-flex align-items-stretch"
          >
            <v-card variant="flat" class="my-4 flex-grow-1 app-content-card">
              <v-card-text class="text-left">
                <h3>{{ $t("candidateTabs.languages.field") }}: {{ language.Language }}</h3>
                <v-divider></v-divider>
                <p><strong>{{ $t("candidateDetails.card.id") }}:</strong> {{ language.id }}</p>
                <p>
                  <strong>{{ $t("candidateTabs.languages.level") }}:</strong>
                  {{ language["Language-level"] }}
                </p>
                <p>
                  <strong>{{ $t("candidateTabs.languages.native") }}:</strong>
                  {{ language["Language-native"] ? $t("candidates.values.yes") : $t("candidates.values.no") }}
                </p>
              </v-card-text>
              <v-card-actions>
                <v-btn icon @click="openEditLanguageDialog(language)">
                  <v-icon color="primary">mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon color="error" @click="confirmDeleteLanguage(language.id)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
        <v-btn
          color="primary"
          @click="openAddLanguageDialog"
          class="mb-4 d-flex align-items-stretch"
        >
          {{ $t("candidateTabs.languages.addButton") }}
        </v-btn>
      </v-card-text>
    </v-card>

    <v-dialog v-model="addLanguageDialog" max-width="600px">
      <v-card>
        <v-card-title>{{ $t("candidateTabs.languages.addTitle") }}</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="addLanguage">
            <v-container>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field :label="`${$t('candidateTabs.languages.field')} *`" v-model="newLanguage.Language" :rules="[rules.required]" outlined required></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field :label="`${$t('candidateTabs.languages.level')} *`" v-model="newLanguage.LanguageLevel" :rules="[rules.required]" outlined required></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select :items="yesNoOptions" item-title="title" item-value="value" :label="$t('candidateTabs.languages.nativeQuestion')" v-model="newLanguage.LanguageNative" :rules="[rules.required]" outlined required></v-select>
                </v-col>
                <v-col cols="12">
                  <v-btn color="primary" type="submit">{{ $t("app.actions.add") }}</v-btn>
                  <v-btn variant="text" @click="closeAddLanguageDialog">{{ $t("app.actions.cancel") }}</v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editLanguageDialog" max-width="600px">
      <v-card>
        <v-card-title>{{ $t("candidateTabs.languages.editTitle") }}</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="updateLanguage">
            <v-container>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field :label="`${$t('candidateTabs.languages.field')} *`" v-model="editLanguage.Language" :rules="[rules.required]" outlined required></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field :label="`${$t('candidateTabs.languages.level')} *`" v-model="editLanguage.LanguageLevel" :rules="[rules.required]" outlined required></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select :items="yesNoOptions" item-title="title" item-value="value" :label="$t('candidateTabs.languages.nativeQuestion')" v-model="editLanguage.LanguageNative" :rules="[rules.required]" outlined required></v-select>
                </v-col>
                <v-col cols="12">
                  <v-btn color="primary" type="submit">{{ $t("app.actions.save") }}</v-btn>
                  <v-btn variant="text" @click="closeEditLanguageDialog">{{ $t("app.actions.cancel") }}</v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { defineComponent } from "vue";

// Languages CRUD tab extracted from CandidateDetails.vue.
// Owns its dialogs + mutations; the parent keeps the list data and re-fetches
// on `refresh`, and renders the shared snackbar on `notify`.
export default defineComponent({
  name: "CandidateLanguagesTab",
  props: {
    candidateId: { type: [String, Number], required: true },
    items: { type: Array, default: () => [] },
  },
  emits: ["refresh", "notify"],
  data() {
    return {
      addLanguageDialog: false,
      newLanguage: { Language: "", LanguageLevel: "", LanguageNative: "" },
      editLanguageDialog: false,
      editLanguage: { id: null, Language: "", LanguageLevel: "", LanguageNative: "" },
    };
  },
  computed: {
    rules() {
      return { required: (value) => !!value || this.$t("candidateTabs.common.requiredField") };
    },
    // Valorile interne rămân "Da"/"Nu" (comparate în payload); doar afișarea e localizată.
    yesNoOptions() {
      return [
        { title: this.$t("candidates.values.yes"), value: "Da" },
        { title: this.$t("candidates.values.no"), value: "Nu" },
      ];
    },
  },
  methods: {
    openAddLanguageDialog() {
      this.newLanguage = { Language: "", LanguageLevel: "", LanguageNative: "Nu" };
      this.addLanguageDialog = true;
    },
    closeAddLanguageDialog() {
      this.addLanguageDialog = false;
    },
    addLanguage() {
      if (!this.newLanguage.Language.trim() || !this.newLanguage.LanguageLevel.trim()) {
        this.$emit("notify", { message: this.$t("candidateTabs.common.requiredEmpty"), color: "error" });
        return;
      }
      const payload = {
        id_foreign: this.candidateId,
        Language: this.newLanguage.Language,
        "Language-level": this.newLanguage.LanguageLevel,
        "Language-native": this.newLanguage.LanguageNative === "Da",
      };
      axios
        .post("/candidati/languages", payload)
        .then(() => {
          this.$emit("notify", { message: this.$t("candidateTabs.languages.added"), color: "success" });
          this.addLanguageDialog = false;
          this.$emit("refresh");
        })
        .catch((error) => {
          console.error("Eroare la adăugarea limbii:", error);
          this.$emit("notify", { message: this.$t("candidateTabs.languages.addError"), color: "error" });
        });
    },
    openEditLanguageDialog(language) {
      this.editLanguage = { ...language };
      this.editLanguageDialog = true;
    },
    closeEditLanguageDialog() {
      this.editLanguageDialog = false;
    },
    updateLanguage() {
      if (!this.editLanguage.Language.trim() || !this.editLanguage.LanguageLevel.trim()) {
        this.$emit("notify", { message: this.$t("candidateTabs.common.requiredEmpty"), color: "error" });
        return;
      }
      const languageId = this.editLanguage.id;
      const payload = {
        id_foreign: this.candidateId,
        Language: this.editLanguage.Language,
        "Language-level": this.editLanguage.LanguageLevel,
        "Language-native": this.editLanguage.LanguageNative === "Da",
      };
      axios
        .put(`/candidati/languages/${languageId}`, payload)
        .then(() => {
          this.$emit("notify", { message: this.$t("candidateTabs.languages.updated"), color: "success" });
          this.editLanguageDialog = false;
          this.$emit("refresh");
        })
        .catch((error) => {
          console.error("Eroare la actualizarea limbii:", error);
          this.$emit("notify", { message: this.$t("candidateTabs.languages.updateError"), color: "error" });
        });
    },
    confirmDeleteLanguage(id) {
      if (confirm(this.$t("candidateTabs.languages.deleteConfirm"))) {
        this.deleteLanguage(id);
      }
    },
    deleteLanguage(id) {
      axios
        .delete(`/candidati/languages/${id}`)
        .then(() => {
          this.$emit("notify", { message: this.$t("candidateTabs.languages.deleted"), color: "success" });
          this.$emit("refresh");
        })
        .catch((error) => {
          console.error("Eroare la ștergerea limbii:", error);
          this.$emit("notify", { message: this.$t("candidateTabs.languages.deleteError"), color: "error" });
        });
    },
  },
});
</script>
